// Centralized training tips configuration
// Each tip should have: id (unique), title, content, action (optional), page, component

export const TRAINING_TIPS = {
  // Profile Page Tips
  profile: {
    training_toggle: {
      id: "profile_training_toggle",
      title: "Training Mode",
      content:
        "You've enabled Training Mode! This will show helpful tips throughout Socrates to help you learn how to use the platform effectively. Look for elements with glowing borders and lightbulb icons - simply hover over them to see helpful tips!",
      action: "You can disable this anytime by toggling the switch off.",
      page: "profile",
      component: "training_toggle",
    },

    avatar_section: {
      id: "profile_avatar",
      title: "Choose Your Avatar",
      content:
        "Select an avatar that represents you! This will be displayed throughout Socrates and help others recognize you.",
      action: "Click on any avatar below to select it, then save your changes.",
      page: "profile",
      component: "avatar_section",
    },

    background_section: {
      id: "profile_background",
      title: "Customize Your Background",
      content:
        "Pick a background theme that you love! This will change the background throughout your Socrates experience.",
      action:
        "Click on any background to preview it, then save to apply permanently.",
      page: "profile",
      component: "background_section",
    },

    password_section: {
      id: "profile_password",
      title: "Account Security",
      content:
        "Keep your account secure by changing your password regularly. You can also log out or request account deletion here.",
      action: 'Click "Change Password" to update your login credentials.',
      page: "profile",
      component: "password_section",
    },

    save_changes: {
      id: "profile_save",
      title: "Save Your Changes",
      content:
        "Don't forget to save your profile changes! Your avatar and background selections will only take effect after you save.",
      action: 'Click the "Update Profile" button to save your changes.',
      page: "profile",
      component: "save_button",
    },
  },

  // Course Management Tips
  courses: {
    welcome: {
      id: "courses_welcome",
      title: "Course Management",
      content:
        "Here you can create, edit, and manage all your courses. Each course can contain multiple sections and modules.",
      page: "courses",
      component: "general",
    },

    create_course: {
      id: "courses_create",
      title: "Create Your First Course",
      content:
        "Start by creating a new course! Give it a descriptive name and add sections to organize your content. Each course can have multiple sections, and each section can contain various learning modules.",
      action: 'Click the "Create New Course" button to get started.',
      page: "courses",
      component: "create_button",
    },
  },

  // Course Detail Page Tips
  course_detail: {
    section_container: {
      id: "course_detail_section",
      title: "Course Sections",
      content:
        "Sections help organize your course content into logical parts (like chapters or units). Each section can contain multiple modules with different types of learning activities.",
      action:
        "Click on any section to see its modules, or use the controls to add, edit, or manage section settings.",
      page: "course_detail",
      component: "section_container",
    },

    module_container: {
      id: "course_detail_module",
      title: "Learning Modules",
      content:
        "Modules are where students access your content! Each module can contain text, images, videos, questions, and interactive activities. Students click on module names to start learning.",
      action:
        "Click on a module name to edit its content, or use the P/Q toggle to switch between Practice and Quiz modes.",
      page: "course_detail",
      component: "module_container",
    },

    add_section: {
      id: "course_detail_add_section",
      title: "Add New Section",
      content:
        "Create a new section to organize more content in your course. Each new section will automatically get a default module to get you started.",
      action: 'Click "Add Section" to create a new course section.',
      page: "course_detail",
      component: "add_section_button",
    },
  },

  // Class List Management Tips
  class_lists: {
    create_class_list: {
      id: "class_lists_create",
      title: "Create a New Class List",
      content:
        "Class lists help you organize and manage groups of students. You can add students manually or import them from a file, then assign these lists to your courses.",
      action:
        'Click the "Create New Class List" button to start organizing your students.',
      page: "class_lists",
      component: "create_button",
    },
  },

  // Module Editor Tips (for future use)
  module_editor: {
    welcome: {
      id: "module_welcome",
      title: "Module Editor",
      content:
        "This is where the magic happens! Create engaging learning content with text, images, videos, and interactive questions.",
      page: "module_editor",
      component: "general",
    },

    add_content: {
      id: "module_add_content",
      title: "Add Learning Content",
      content:
        "Use these buttons to add different types of content to your module. Mix and match to create engaging lessons!",
      action: 'Try clicking "Add Media" to upload an image or video.',
      page: "module_editor",
      component: "content_buttons",
    },

    save_module: {
      id: "module_save",
      title: "Save Your Work",
      content:
        "Remember to save your module regularly to avoid losing your changes!",
      action: 'Click "Save Changes" to save your progress.',
      page: "module_editor",
      component: "save_button",
    },

    generate_content: {
      id: "module_generate",
      title: "AI Content Generation",
      content:
        "Let AI help you create content! Describe what you want to teach and AI will generate questions, text, and activities for your module.",
      action:
        'Click "Generate Content" to open the AI content creation assistant.',
      page: "module_editor",
      component: "generate_button",
    },

    settings: {
      id: "module_settings",
      title: "Module Settings",
      content:
        "Configure your module settings here. You can enable quiz mode, toggle AI chat assistance for students, and manage question categories.",
      action: 'Click "Settings" to customize your module behavior.',
      page: "module_editor",
      component: "settings_button",
    },
  },

  // Navigation Tips
  navigation: {
    header: {
      id: "nav_header",
      title: "Navigation Hub",
      content:
        "Use the navigation bar to quickly move between different sections of Socrates. Each icon represents a different area of the platform - from managing courses to viewing gradebooks and accessing AI tutors.",
      action:
        "Click on any navigation item to explore that section of Socrates.",
      page: "general",
      component: "header",
    },
  },
};

// Helper functions to get tips
export const getTipsByPage = (page) => {
  const tips = [];
  Object.keys(TRAINING_TIPS).forEach((category) => {
    Object.keys(TRAINING_TIPS[category]).forEach((tipKey) => {
      const tip = TRAINING_TIPS[category][tipKey];
      if (tip.page === page) {
        tips.push(tip);
      }
    });
  });
  return tips;
};

export const getTipById = (id) => {
  for (const category of Object.values(TRAINING_TIPS)) {
    for (const tip of Object.values(category)) {
      if (tip.id === id) {
        return tip;
      }
    }
  }
  return null;
};

export const getTipsByComponent = (page, component) => {
  const tips = [];
  Object.keys(TRAINING_TIPS).forEach((category) => {
    Object.keys(TRAINING_TIPS[category]).forEach((tipKey) => {
      const tip = TRAINING_TIPS[category][tipKey];
      if (tip.page === page && tip.component === component) {
        tips.push(tip);
      }
    });
  });
  return tips;
};

// Profile page specific tips in order of appearance
export const PROFILE_PAGE_TIPS = [
  TRAINING_TIPS.profile.training_toggle,
  TRAINING_TIPS.profile.avatar_section,
  TRAINING_TIPS.profile.background_section,
  TRAINING_TIPS.profile.password_section,
  TRAINING_TIPS.profile.save_changes,
];
